import { User } from "~/common/Store/models/User";
import isActiveSale from "./isActiveSale";
import { QuantityDiscount } from "~/common/Store/models/ProductPage";
import shouldDisplayQuantityDiscounts from "~/ProductDetails/Pricing/shouldDisplayQuantityDiscounts";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";

interface Price {
  minimumAdvertisedPriceOverride: {
    priceLevel5: number;
  };
  minimumAdvertisedPriceProperties: {
    price: number;
    type: string;
  };
  salePrice: number;
  price: number;
  displaySalePrice: boolean;
  saleStartDate?: string;
  saleEndDate?: string;
  quantityDiscounts?: QuantityDiscount[];
}

interface LowestPricingQuantityDiscount {
  endQuantity?: number;
  isLotDiscount: boolean;
  quantity: number;
  price: number;
  startQuantity: number;
}

interface LowestPricingProduct {
  customizationRequired: boolean;
  customizationUrl: string;
  isCustomizable: boolean;
  minimumCustomizationPrice: number;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  startBuy: number;
  price: {
    minimumAdvertisedPriceProperties: {
      price: number;
      type: string;
    };
    minimumAdvertisedPriceOverride: {
      priceLevel5: number;
    };
    quantityDiscounts: LowestPricingQuantityDiscount[];
    displaySalePrice: boolean;
    saleStartDate?: string;
    saleEndDate?: string;
    salePrice: number;
    price: number;
    displayMinimumAdvertisedPrice: boolean;
  };
}
// This method get the lowest from among p1, p4, and sale price.
const getLowestPrice = (
  price: Price,
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean,
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean,
  user: User,
  showPlusPricing = false
): number => {
  const activeSale = isActiveSale(price);

  const hasMAPStyle =
    price.minimumAdvertisedPriceProperties &&
    !!price.minimumAdvertisedPriceProperties.type?.trim();

  const hasPlusPricing =
    !!price.minimumAdvertisedPriceOverride?.priceLevel5 && user.hasWebPlus;

  let lowestPrice = Math.min(
    activeSale ? price.salePrice : Infinity,
    price.price
  );

  if (hasMAPStyle) {
    if (user.registered) {
      if (
        showBelowMinimumAdvertisedPriceForLoggedInCustomers ||
        (showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
          (user.hasWebPlus || user.platinum?.enabled))
      ) {
        lowestPrice = Math.min(
          price.minimumAdvertisedPriceProperties.price || Infinity,
          lowestPrice
        );
      }
    }

    if (price.minimumAdvertisedPriceProperties.type?.trim() === "G") {
      lowestPrice = Math.min(
        price.minimumAdvertisedPriceProperties.price || Infinity,
        lowestPrice
      );
    }
  }

  if (showPlusPricing && hasPlusPricing) {
    lowestPrice = Math.min(
      price.minimumAdvertisedPriceOverride.priceLevel5 || Infinity,
      lowestPrice
    );
  }

  return lowestPrice;
};

export const getLowestPriceWithQuantityDiscounts = (
  product: LowestPricingProduct,
  user: User,
  isGoogleView = false
): number => {
  const {
    price,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    minimumCustomizationPrice = 0,
  } = product;

  if (shouldDisplayRequiredCustomization(product)) {
    return minimumCustomizationPrice;
  }

  let lowestPrice = getLowestPrice(
    price,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    user
  );

  const showPlusPrice =
    !!price.minimumAdvertisedPriceOverride?.priceLevel5 &&
    price.minimumAdvertisedPriceOverride.priceLevel5 < lowestPrice &&
    user.hasWebPlus;

  if (showPlusPrice) {
    lowestPrice =
      price.minimumAdvertisedPriceOverride.priceLevel5 < lowestPrice
        ? price.minimumAdvertisedPriceOverride.priceLevel5
        : lowestPrice;
  }

  const [display, quantityDiscounts] = shouldDisplayQuantityDiscounts(
    product,
    user
  );

  if (!display) {
    return lowestPrice;
  }

  const lowestQuantityDiscount = quantityDiscounts?.sort((a, b) => {
    if (a.price < b.price) {
      return -1;
    }

    if (a.price > b.price) {
      return 1;
    }

    return 0;
  })[0]?.price;

  if (isGoogleView && !user.registered) {
    return lowestPrice;
  }

  return lowestQuantityDiscount && lowestQuantityDiscount < lowestPrice
    ? lowestQuantityDiscount
    : lowestPrice;
};

export default getLowestPrice;
